import * as React from 'react';

import { Link } from 'react-router-dom';
import { Transition, Menu } from '@headlessui/react';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe, constNull } from 'fp-ts/function';
import { Heart, User } from 'react-feather';
import { FeatherIcon } from '../components/FeatherIcon';
import { useAuth } from '../contexts/AuthContext';

import { Heading } from '../components/Heading';
import { Container } from '../components/Container';
import { paths } from '../paths';
import { InternalLink } from '../components/InternalLink';
import { PremiumSpark } from '../icons/PremiumSpark';
import { useBillingSession } from '../subscriptions/useBillingSession';
import { useTeam } from '../contexts/TeamContext';
import { isNil } from '../utils/isNil';
import { usePreventBodyScroll } from '../hooks/usePreventBodyScroll';

const baseMenuItemClass = (active = false) =>
  `w-full flex items-center space-x-2 w-full py-3 leading-5 text-left leading-5 transition ease-in duration-75 xs:text-normal text-lg hover:text-primary-300 ${
    active ? 'text-primary-300' : ''
  }`;
const menuClickableClass = (active = false) =>
  `${baseMenuItemClass(active)} font-extralight`;
const bottomMenuClassName = baseMenuItemClass;

interface PopupMenuProps {
  open: boolean;
}

const ManageSubscriptionMenuLink: React.FC<{ active: boolean }> = ({
  active,
}) => {
  const { navigateToBillingPortal } = useBillingSession();
  return (
    <button
      type="button"
      onClick={navigateToBillingPortal}
      className="block text-left w-full "
    >
      <div className={menuClickableClass(active)}>
        <PremiumSpark className="text-amber-500" />

        <p className="font-semibold text-lg">You're a Premium user </p>
      </div>

      <p className="text-sm text-primary-200 ml-8 -mt-3">
        Manage your subscription
      </p>
    </button>
  );
};

export const HeaderMenu = ({ open }: PopupMenuProps) => {
  const { user, logout } = useAuth();
  usePreventBodyScroll(open);
  const isLoggedIn = !isNil(user);

  const { team } = useTeam();

  const teamForAdmin = pipe(
    team,
    O.filter((t) => t.isCaptain),
  );

  const teamWithOutstandingJoinRequests = pipe(
    teamForAdmin,
    O.filter((team) => !A.isEmpty(team.requests)),
  );

  return (
    <>
      <Menu.Button className="block bg-transparent w-6" title="menu">
        {O.isSome(teamWithOutstandingJoinRequests) ? (
          <span
            className="bg-amber-500 w-3 h-3 rounded-full block absolute border-primary border-2"
            style={{ right: -4, top: 3 }}
          />
        ) : null}
        <FeatherIcon icon="menu" />
      </Menu.Button>

      <Transition
        show={open}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          style={{ marginTop: 'calc(3.5rem + env(safe-area-inset-top))' }}
          className="z-50 fixed inset-0 text-white bg-primary px-4 sm:py-6 py-8 md:flex md:items-center focus:outline-none overflow-y-auto"
        >
          <Container>
            <Menu.Items static className="focus:outline-none space-y-6">
              {pipe(
                team,
                O.fold(
                  () => null,
                  (t) => (
                    <div className="space-y-2">
                      <Heading level={1} className="text-4xl">
                        {t.name}
                      </Heading>
                      {t.club ? (
                        <p className="font-light">{t.club.name}</p>
                      ) : null}
                    </div>
                  ),
                ),
              )}
              <div>
                {isLoggedIn && (
                  <>
                    {pipe(
                      teamWithOutstandingJoinRequests,
                      O.fold(constNull, (team) => (
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              key={team.id}
                              className={menuClickableClass(active)}
                              to={paths.teamSettings.joinRequests.home()}
                            >
                              <FeatherIcon icon="inbox" className="stroke-1" />
                              <div className="flex items-center">
                                Join Requests
                                <span className="bg-amber-600 w-2 h-2 rounded-full block ml-2" />
                              </div>
                            </Link>
                          )}
                        </Menu.Item>
                      )),
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to={paths.account.home()}
                          className={menuClickableClass(active)}
                        >
                          <FeatherIcon icon="user" className="stroke-1" />
                          <span>Account</span>
                        </Link>
                      )}
                    </Menu.Item>
                  </>
                )}

                {pipe(
                  teamForAdmin,
                  O.fold(constNull, () => (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          className={menuClickableClass(active)}
                          to={paths.teamSettings.home()}
                        >
                          <FeatherIcon icon="sliders" className="stroke-1" />
                          <span>Team settings</span>
                        </Link>
                      )}
                    </Menu.Item>
                  )),
                )}

                {pipe(
                  team,
                  O.filter((t) => !isNil(t.player)),
                  O.fold(constNull, () => {
                    return (
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            className={menuClickableClass(active)}
                            to={paths.playerSettings.home()}
                          >
                            <User className="stroke-1" />
                            <span>Player settings</span>
                          </Link>
                        )}
                      </Menu.Item>
                    );
                  }),
                )}
              </div>

              <Menu.Item>
                {({ active }) => (
                  <Link
                    to={paths.referrals.shareLink()}
                    className="block text-left w-full "
                  >
                    <div className={menuClickableClass(active)}>
                      <Heart className="text-red-500 fill-current" />

                      <p className="font-semibold text-lg">Invite a friend</p>
                    </div>

                    <p className="text-sm text-primary-200 ml-8 -mt-3">
                      Get £4 off Premium
                    </p>
                  </Link>
                )}
              </Menu.Item>

              {!isNil(user) ? (
                <Menu.Item as="div">
                  {({ active }) =>
                    user?.isPremium ? (
                      <ManageSubscriptionMenuLink active={active} />
                    ) : (
                      <InternalLink to={paths.subscriptions.home()}>
                        <div className={menuClickableClass(active)}>
                          <PremiumSpark className="text-amber-500" />

                          <p className="font-semibold text-lg">
                            Matchhaus Premium
                          </p>
                        </div>
                        <p className="text-sm text-primary-200 ml-8 -mt-3">
                          Unlock your stats and rankings
                        </p>
                      </InternalLink>
                    )
                  }
                </Menu.Item>
              ) : null}

              <div>
                <Menu.Item>
                  {({ active }) => (
                    <Link className={bottomMenuClassName(active)} to="/help">
                      Help
                    </Link>
                  )}
                </Menu.Item>

                {isLoggedIn ? (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={bottomMenuClassName(active)}
                        onClick={logout}
                        type="button"
                      >
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                ) : (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        className={bottomMenuClassName(active)}
                        to={paths.signIn()}
                      >
                        Sign in
                      </Link>
                    )}
                  </Menu.Item>
                )}
              </div>
            </Menu.Items>
          </Container>
        </div>
      </Transition>
    </>
  );
};
